import React from "react";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import { CircleDot, Users, UserSquare, Shield, Trophy } from "lucide-react";
import CosmicBackground from "./CosmicBackground";
import CollectiveFlowOrbs from "./decor/CollectiveFlowOrbs";

const FOR_ICONS = [UserSquare, Shield, Users, Trophy];

const FutureFootball = () => {
  const { t } = useTranslation();
  const bullets = t("future.bullets", { returnObjects: true });
  const forItems = t("future.for_items", { returnObjects: true });

  return (
    <section
      data-testid="future-section"
      className="relative section-pad overflow-hidden"
    >
      <CosmicBackground variant="football" />

      {/* Collective Flow visual — players moving as one organism */}
      <div aria-hidden className="pointer-events-none absolute inset-0 flex items-center justify-center opacity-30">
        <CollectiveFlowOrbs className="w-[80%] max-w-[900px] h-[80%]" opacity={0.55} />
      </div>

      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="mx-auto max-w-4xl text-center">
          <span className="eyebrow">{t("future.eyebrow")}</span>
          <h2 className="font-display mt-5 text-xl sm:text-[1.5rem] lg:text-[1.65rem] font-semibold leading-[1.18] text-white glow-text-white uppercase tracking-[-0.005em]">
            {t("future.title")}
          </h2>
          <p className="mx-auto mt-6 max-w-2xl text-base md:text-lg text-white/70 leading-relaxed">
            {t("future.lead")}
          </p>
        </div>

        <div className="mt-14 grid gap-4 md:grid-cols-2">
          {Array.isArray(bullets) &&
            bullets.map((b, i) => (
              <motion.div
                key={i}
                data-testid={`future-bullet-${i}`}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: i * 0.08 }}
                className="flex items-center gap-4 rounded-xl glass px-6 py-5"
              >
                <CircleDot className="h-5 w-5 text-[#FF3DCB] flex-shrink-0" />
                <span className="font-display text-base md:text-lg text-white/90">
                  {b}
                </span>
              </motion.div>
            ))}
        </div>

        <div className="mt-20">
          <h3 className="font-display text-center text-2xl md:text-3xl font-bold text-white">
            {t("future.for_title")}
          </h3>
          <div className="mt-10 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
            {Array.isArray(forItems) &&
              forItems.map((it, i) => {
                const Icon = FOR_ICONS[i % FOR_ICONS.length];
                return (
                  <motion.div
                    key={i}
                    data-testid={`future-for-${i}`}
                    initial={{ opacity: 0, y: 24 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-60px" }}
                    transition={{ duration: 0.55, delay: i * 0.08 }}
                    className="group relative overflow-hidden rounded-2xl glass p-7 electric-border"
                  >
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-white/10 bg-gradient-to-br from-[#9B4DFF]/20 to-transparent">
                      <Icon className="h-6 w-6 text-[#6FF6FF]" />
                    </div>
                    <h4 className="font-display mt-5 text-xl font-semibold text-white">
                      {it.h}
                    </h4>
                    <p className="mt-2 text-sm leading-relaxed text-white/65">
                      {it.p}
                    </p>
                  </motion.div>
                );
              })}
          </div>
        </div>

        <div className="mt-16 text-center">
          <p className="font-display mx-auto max-w-3xl text-xl md:text-2xl font-semibold leading-snug">
            <span className="headline-neon">{t("future.tagline")}</span>
          </p>
        </div>
      </div>
    </section>
  );
};

export default FutureFootball;
